import { HeaderFooterLayout } from '../app';
import ContentfulPage from './ContentfulPage';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { TypeBlogArticle, TypePage } from '@platform/contentful';
import ErrorPage from '../../ErrorPage';
import { useRoute } from 'wouter';
import ContentfulBlogArticle from './ContentfulBlogArticle';
import BlogHomePage from './blog';
import { ContentfulRootData } from './util/types';
import { CategoryDirectory } from './blog/CategoryDirectory';
import { ContentfulData, loadContentfulData } from './util/utils';

/** The component rendered to serve any page backed with Contentful data. Note that this is only
 * rendered by the SPA, and typically only executed during local dev. Page loads for Contentful pages
 * outside of local dev are requested from the Caddy server, and we treat Contentful pages as "external"
 * (ie: don't typically navigate to them via SPA-level Link routes, etc.). This is because they require
 * a lot of data pulled at build-time from Contentful, and it's inefficiently to dynamically re-request it
 * at runtime.
 *
 * If this proves overly complex, we could use Contentful hook to live-request data, being careful to
 * convert into `BlogArticleSummary` types for the summary pages where appropriate. */
const ContentfulTarget = ({ contentfulData }: { contentfulData: ContentfulRootData | null }) => {
  const [_match, params] = useRoute('*');
  const route = '/' + (params?.['*'] ?? '');

  let pageData: ContentfulData | null = null;
  if (contentfulData && params) {
    pageData = loadContentfulData(route, contentfulData);
  }

  if (typeof window !== 'undefined' && 'CONTENTFUL_PAGE_DATA' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pageData = (window as any)['CONTENTFUL_PAGE_DATA'] as ContentfulData;
  }

  if (!pageData) {
    return <ErrorPage />;
  }

  switch (pageData.type) {
    case 'blog-category':
      return (
        <CategoryDirectory
          // Will be null if categorySlug === 'all'
          category={pageData.data.category}
          allCategories={pageData.data.allCategories}
          articleSummaries={pageData.data.summaries}
        />
      );
    case 'blog-home':
      return <BlogHomePage blogArticleSummaries={pageData.data} />;
    case 'blog-article':
    case 'contentful-page':
      return (
        <HeaderFooterLayout>
          <ContentfulLivePreviewProvider locale='en-US'>
            {pageData.data.sys.contentType.sys.id === 'blogArticle' && (
              <ContentfulBlogArticle
                articleEntry={
                  pageData.data as TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>
                }
              />
            )}
            {pageData.data.sys.contentType.sys.id === 'page' && (
              <ContentfulPage
                pageEntry={pageData.data as TypePage<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>}
              />
            )}
          </ContentfulLivePreviewProvider>
        </HeaderFooterLayout>
      );
  }
};

export default ContentfulTarget;
